import type { CMChecklistManualOverride, CMChecklistStaleManualOverride } from '~/services/OffersService';

import type { GeneratedChecklistRow } from './checklistRows';
import type { ManualOverrideDecision, ManualOverrideState } from './types';

type ApprovalSource = 'auto' | 'manual' | 'manual_required' | 'none';

export function buildManualOverrideState({
  manualOverridesFromApi,
  rows,
}: {
  manualOverridesFromApi: Array<CMChecklistManualOverride>;
  rows: Array<GeneratedChecklistRow>;
}): ManualOverrideState {
  const rowsByItemKey = new Map(rows.map((row) => [row.itemKey, row]));

  return manualOverridesFromApi.reduce<ManualOverrideState>((nextOverrides, manualOverride) => {
    const row = rowsByItemKey.get(manualOverride.item_key);

    if (row?.evidenceHash === manualOverride.evidence_hash) {
      nextOverrides[row.id] = normalizeManualOverrideStatus(manualOverride);
    }

    return nextOverrides;
  }, {});
}

export function getApprovalSource(
  row: Pick<GeneratedChecklistRow, 'id' | 'status'>,
  manualOverrides: ManualOverrideState,
): ApprovalSource {
  const manualOverrideStatus = getManualOverrideStatus(row, manualOverrides);

  if (manualOverrideStatus === 'approved') {
    return 'manual';
  }

  if (manualOverrideStatus === 'requires_approval') {
    return 'manual_required';
  }

  return row.status === 'approved' ? 'auto' : 'none';
}

export function getManualOverrideStatus(
  row: Pick<GeneratedChecklistRow, 'id'>,
  manualOverrides: ManualOverrideState,
): ManualOverrideDecision | undefined {
  return manualOverrides[row.id];
}

export function isRowApproved(row: Pick<GeneratedChecklistRow, 'id' | 'status'>, manualOverrides: ManualOverrideState) {
  const approvalSource = getApprovalSource(row, manualOverrides);

  return approvalSource === 'auto' || approvalSource === 'manual';
}

export function isRowManuallyApproved(row: Pick<GeneratedChecklistRow, 'id'>, manualOverrides: ManualOverrideState) {
  return getManualOverrideStatus(row, manualOverrides) === 'approved';
}

export function isRowManuallyRequired(row: Pick<GeneratedChecklistRow, 'id'>, manualOverrides: ManualOverrideState) {
  return getManualOverrideStatus(row, manualOverrides) === 'requires_approval';
}

export function getEffectiveChecklistStatus(
  row: Pick<GeneratedChecklistRow, 'id' | 'status'>,
  manualOverrides: ManualOverrideState,
) {
  if (isRowApproved(row, manualOverrides)) {
    return 'approved';
  }

  if (isRowManuallyRequired(row, manualOverrides)) {
    return 'requires approval';
  }

  return row.status;
}

export function buildStaleManualOverrides({
  manualOverridesFromApi,
  rows,
}: {
  manualOverridesFromApi: Array<CMChecklistManualOverride>;
  rows: Array<GeneratedChecklistRow>;
}): Array<CMChecklistStaleManualOverride> {
  const generatedItemKeys = new Set(rows.map((row) => row.itemKey));

  return manualOverridesFromApi
    .filter((manualOverride) => !generatedItemKeys.has(manualOverride.item_key))
    .map((manualOverride) => ({
      id: manualOverride.id,
      item_key: manualOverride.item_key,
      updated_at: manualOverride.updated_at,
    }));
}

function normalizeManualOverrideStatus(manualOverride: CMChecklistManualOverride): ManualOverrideDecision {
  return manualOverride.is_approved ? 'approved' : 'requires_approval';
}
